import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { getAuthName, getAuthHeaders, getAuthEmail, logout as authLogout, userKey } from "../utils/auth.js";
import { pullAndMerge, pushAssessments } from "../utils/syncAssessments.js";
import { payForDiagnosis, loadRazorpay } from "../utils/payDiagnosis.js";
import API_BASE from "../api.js";
import OrthoPrintSummary from "../components/OrthoPrintSummary.js";
import NeuroPrintSummary from "../components/NeuroPrintSummary.js";
import CardioPrintSummary from "./cardio/components/PrintSummary.js";

const ADMIN_EMAILS = ['itihassabkasakshihai@gmail.com', 'rkps7847@gmail.com'];

const SAVED_ORTHO_KEY  = 'saved-ortho-assessments';
const SAVED_NEURO_KEY  = 'saved-neuro-assessments';
const SAVED_CARDIO_KEY = 'saved-cardio-assessments';
const ORTHO_DRAFT_KEY  = 'subjective-ortho-assessment-draft';
const NEURO_DRAFT_KEY  = 'neuro-assessment-draft';
const CARDIO_DRAFT_KEY = 'cardio-draft';

const NEURO_COLORS = {
  'UMN Pattern': { color: '#6366f1', bg: '#eef2ff' },
  'LMN Pattern': { color: '#10b981', bg: '#ecfdf5' },
  'Cerebellar':  { color: '#8b5cf6', bg: '#f5f3ff' },
  'Sensory':     { color: '#f59e0b', bg: '#fffbeb' },
};

/* ════════════════════════════════════════════════════════════
   MODULE CARD
════════════════════════════════════════════════════════════ */
const ModuleCard = ({ icon, title, desc, accentColor, accentBg, features, onNew, comingSoon, count }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      className="am-module-card"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: '#fff',
        borderRadius: 18,
        border: `1.5px solid ${hov && !comingSoon ? accentColor + '55' : '#e8edf3'}`,
        boxShadow: hov && !comingSoon ? `0 8px 28px ${accentColor}18` : '0 1px 4px rgba(15,23,42,0.05)',
        transition: 'all 0.2s',
        transform: hov && !comingSoon ? 'translateY(-3px)' : 'none',
        padding: 22,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* top row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div className="am-module-card-icon" style={{ width: 42, height: 42, borderRadius: 12, background: accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
          {icon}
        </div>
        {comingSoon ? (
          <span className="am-module-badge" style={{ fontSize: '0.62rem', fontWeight: 800, padding: '2px 9px', borderRadius: 99, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', letterSpacing: '0.06em' }}>
            SOON
          </span>
        ) : (
          <span className="am-module-badge" style={{ fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: accentBg, color: accentColor }}>
            {count} record{count !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* title + desc */}
      <div>
        <h3 className="am-module-card-title" style={{ margin: '0 0 4px', fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>{title}</h3>
        <p className="am-module-desc" style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.5 }}>{desc}</p>
      </div>

      {/* feature pills */}
      <div className="am-module-pills" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {features.map(f => (
          <span key={f} style={{ fontSize: '0.65rem', fontWeight: 600, padding: '3px 9px', borderRadius: 99, background: accentBg, color: accentColor }}>
            {f}
          </span>
        ))}
      </div>

      {/* button */}
      <button
        className="am-module-btn"
        onClick={onNew}
        disabled={comingSoon}
        style={{
          marginTop: 'auto',
          width: '100%',
          padding: '10px',
          minHeight: 44,
          borderRadius: 12,
          border: 'none',
          background: comingSoon ? '#f1f5f9' : accentColor,
          color: comingSoon ? '#94a3b8' : '#fff',
          fontWeight: 700,
          fontSize: '0.82rem',
          cursor: comingSoon ? 'not-allowed' : 'pointer',
          boxShadow: comingSoon ? 'none' : `0 4px 14px ${accentColor}35`,
          transition: 'opacity 0.15s',
        }}
        onMouseEnter={e => { if (!comingSoon) e.currentTarget.style.opacity = '0.85'; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
      >
        {comingSoon ? 'Coming Soon' : (
          <>
            <span className="am-btn-long">{`+ New ${title}`}</span>
            <span className="am-btn-short">Start →</span>
          </>
        )}
      </button>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   HISTORY ROW
════════════════════════════════════════════════════════════ */
const HistoryRow = ({ record, module, tagColor, tagBg, onOpen, onDelete, onPrint }) => {
  const [hov, setHov] = useState(false);
  const [delHov, setDelHov] = useState(false);
  const [printHov, setPrintHov] = useState(false);
  return (
    <div
      className="am-history-row"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ background: hov ? '#f8fafc' : '#fff', transition: 'background 0.12s', borderBottom: '1px solid #f1f5f9' }}
    >
      <div className="am-td-module" onClick={onOpen} style={{ padding: '11px 12px 11px 20px', cursor: 'pointer' }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '3px 9px', borderRadius: 6, background: tagBg, color: tagColor }}>
          {module}
        </span>
      </div>
      <div className="am-td-patient" onClick={onOpen} style={{ padding: '11px 12px', cursor: 'pointer', minWidth: 0 }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.patientName}</span>
      </div>
      <div className="am-td-zone" onClick={onOpen} style={{ padding: '11px 12px', cursor: 'pointer', minWidth: 0 }}>
        <span style={{ fontSize: '0.78rem', color: '#64748b', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.zone || record.pattern}</span>
      </div>
      <div className="am-td-diag" onClick={onOpen} style={{ padding: '11px 12px', cursor: 'pointer', minWidth: 0 }}>
        <span style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {record.diagnosis || '—'}
        </span>
      </div>
      <div className="am-td-date" onClick={onOpen} style={{ padding: '11px 12px', cursor: 'pointer' }}>
        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{record.date}</span>
      </div>
      <div className="am-td-status" onClick={onOpen} style={{ padding: '11px 12px', cursor: 'pointer' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          fontSize: '0.72rem', fontWeight: 700,
          padding: '3px 10px', borderRadius: 99,
          background: record.isSubmitted ? '#ecfdf5' : '#fffbeb',
          color: record.isSubmitted ? '#059669' : '#d97706',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: record.isSubmitted ? '#10b981' : '#f59e0b' }} />
          {record.isSubmitted ? 'Diagnosed' : 'Draft'}
        </span>
      </div>
      <div className="am-td-actions" style={{ padding: '11px 20px 11px 12px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <button
            onClick={e => { e.stopPropagation(); onPrint(); }}
            onMouseEnter={() => setPrintHov(true)}
            onMouseLeave={() => setPrintHov(false)}
            title="Download PDF"
            style={{
              background: printHov ? '#f0fdf4' : 'transparent',
              border: `1px solid ${printHov ? '#86efac' : 'transparent'}`,
              color: printHov ? '#16a34a' : '#cbd5e1',
              borderRadius: 8, width: 52, height: 44,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3,
              cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, transition: 'all 0.15s', flexShrink: 0,
            }}
          >
            <span style={{ color: printHov ? '#16a34a' : '#86efac', fontSize: '0.85rem' }}>↓</span> PDF
          </button>
          <button
            onClick={e => { e.stopPropagation(); onDelete(); }}
            onMouseEnter={() => setDelHov(true)}
            onMouseLeave={() => setDelHov(false)}
            title="Delete assessment"
            style={{
              background: delHov ? '#fef2f2' : 'transparent',
              border: `1px solid ${delHov ? '#fca5a5' : 'transparent'}`,
              color: delHov ? '#ef4444' : '#cbd5e1',
              borderRadius: 8, width: 44, height: 44,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontSize: '1rem', transition: 'all 0.15s', flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};


/* ════════════════════════════════════════════════════════════
   HOMEPAGE
════════════════════════════════════════════════════════════ */
const HomePage = () => {
  const navigate = useNavigate();
  const [orthoRecords,  setOrthoRecords]  = useState([]);
  const [neuroRecords,  setNeuroRecords]  = useState([]);
  const [cardioRecords, setCardioRecords] = useState([]);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [printRecord, setPrintRecord] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageLoaded, setUsageLoaded] = useState(false);
  const [usageFailed, setUsageFailed] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeModule, setUpgradeModule] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [payError, setPayError] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [pendingPaidId, setPendingPaidId] = useState(null);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const installed = () => setInstallPrompt(null);
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setInstallPrompt(null);
  };

  const handlePrint = (r) => {
    let record = r;
    if (r._module === 'Ortho') {
      const d = r.data || {};
      const src = typeof d.diagnosis === 'string' ? { primary: d.diagnosis } : (d.diagnosis || {});
      record = { ...r, diagnosisData: src };
    }
    flushSync(() => setPrintRecord(record));
    const cleanup = () => { setPrintRecord(null); window.removeEventListener('afterprint', cleanup); };
    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  useEffect(() => {
    try { const o = window.localStorage.getItem(userKey(SAVED_ORTHO_KEY));  if (o) setOrthoRecords(JSON.parse(o));  } catch { /**/ }
    try { const n = window.localStorage.getItem(userKey(SAVED_NEURO_KEY));  if (n) setNeuroRecords(JSON.parse(n));  } catch { /**/ }
    try { const c = window.localStorage.getItem(userKey(SAVED_CARDIO_KEY)); if (c) setCardioRecords(JSON.parse(c)); } catch { /**/ }

    pullAndMerge(SAVED_ORTHO_KEY,  SAVED_ORTHO_KEY).then(r  => { if (r) setOrthoRecords(r);  });
    pullAndMerge(SAVED_NEURO_KEY,  SAVED_NEURO_KEY).then(r  => { if (r) setNeuroRecords(r);  });
    pullAndMerge(SAVED_CARDIO_KEY, SAVED_CARDIO_KEY).then(r => { if (r) setCardioRecords(r); });

    fetch(`${API_BASE}/api/user/usage`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { setUsage(d); setUsageFailed(false); })
      .catch(() => setUsageFailed(true))
      .finally(() => setUsageLoaded(true));

    fetch(`${API_BASE}/api/payment/pending-diagnosis`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.assessmentId) setPendingPaidId(d.assessmentId); })
      .catch(() => {});
  }, []);

  const openOrtho = r => {
    window.localStorage.setItem(userKey(ORTHO_DRAFT_KEY), JSON.stringify({ currentStep: 4, activeZone: r.zone, isSubmitted: r.isSubmitted || false, chatHistory: r.chatHistory || [], chatInput: '', formData: r.data, assessmentId: r.id }));
    navigate('/ortho-form');
  };
  const openNeuro = r => {
    window.localStorage.setItem(userKey(NEURO_DRAFT_KEY), JSON.stringify({ currentStep: 4, activePattern: r.pattern, isSubmitted: r.isSubmitted || false, chatHistory: r.chatHistory || [], chatInput: '', formData: r.data, assessmentId: r.id, diagnosisData: r.diagnosisData || {} }));
    navigate('/neuro-form');
  };
  const PLAN_LABELS = { student_yearly: 'Student Yearly', clinician_yearly: 'Clinician Yearly', diagnosis_single: 'AI Diagnosis — ₹19' };

  const handlePayment = async (planId, assessmentId, onSuccess) => {
    setPaymentLoading(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { setPayError('Could not load payment gateway. Check your internet connection.'); setPaymentLoading(false); return; }

      const res = await fetch(`${API_BASE}/api/payment/create-order`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ planId, assessmentId }),
      });
      if (!res.ok) { const e = await res.json(); setPayError(e.error || 'Could not create order.'); setPaymentLoading(false); return; }

      const { orderId, amount, currency } = await res.json();
      const options = {
        key:         process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount, currency,
        name:        'AssessMonk',
        description: PLAN_LABELS[planId] || planId,
        order_id:    orderId,
        handler: async (response) => {
          setPaymentLoading(true);
          try {
            const vRes = await fetch(`${API_BASE}/api/payment/verify`, {
              method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ ...response }),
            });
            if (!vRes.ok) {
              const e = await vRes.json();
              setPayError(e.error || 'Verification failed. Contact support if amount was deducted.');
            } else {
              setShowUpgrade(false);
              setSelectedPlan(null);
              setUpgradeModule(null);
              setPayError(null);
              const uRes = await fetch(`${API_BASE}/api/user/usage`, { headers: getAuthHeaders() });
              if (uRes.ok) setUsage(await uRes.json());
              if (onSuccess) onSuccess(await vRes.json());
            }
          } catch { setPayError('Verification error. Contact support if amount was deducted.'); }
          finally   { setPaymentLoading(false); }
        },
        prefill: { email: getAuthEmail() },
        theme:   { color: '#4f46e5' },
        modal:   { ondismiss: () => setPaymentLoading(false) },
      };
      new window.Razorpay(options).open();
    } catch { setPayError('Payment failed. Please try again.'); setPaymentLoading(false); }
  };

  const isAtLimit = () => usage && !usage.isAdmin && usage.tier === 'free' && usage.used >= usage.limit;

  const newOrtho = () => {
    if (!usageLoaded) return;
    if (isAtLimit()) { setUpgradeModule('ortho'); setShowUpgrade(true); return; }
    window.localStorage.removeItem(userKey(ORTHO_DRAFT_KEY)); navigate('/ortho-form');
  };
  const newNeuro = () => {
    if (!usageLoaded) return;
    if (isAtLimit()) { setUpgradeModule('neuro'); setShowUpgrade(true); return; }
    window.localStorage.removeItem(userKey(NEURO_DRAFT_KEY)); navigate('/neuro-form');
  };
  const newCardio = () => {
    if (!usageLoaded) return;
    if (isAtLimit()) { setUpgradeModule('cardio'); setShowUpgrade(true); return; }
    window.localStorage.removeItem(userKey(CARDIO_DRAFT_KEY)); navigate('/cardio-form');
  };
  const navigateToFormAfterPay = (newId) => {
    const mod = upgradeModule || 'ortho';
    const route = mod === 'ortho' ? '/ortho-form' : mod === 'neuro' ? '/neuro-form' : '/cardio-form';
    if (mod === 'cardio') {
      window.localStorage.setItem(userKey(CARDIO_DRAFT_KEY), JSON.stringify({ assessmentId: newId, _prepaid: true }));
    } else {
      const draftKey = mod === 'ortho' ? ORTHO_DRAFT_KEY : NEURO_DRAFT_KEY;
      window.localStorage.setItem(userKey(draftKey), JSON.stringify({ assessmentId: newId }));
    }
    setShowUpgrade(false);
    setSelectedPlan(null);
    navigate(route);
  };

  const resumePaidSession = (mod) => {
    const route = mod === 'ortho' ? '/ortho-form' : mod === 'neuro' ? '/neuro-form' : '/cardio-form';
    if (mod === 'cardio') {
      window.localStorage.setItem(userKey(CARDIO_DRAFT_KEY), JSON.stringify({ assessmentId: pendingPaidId, _prepaid: true }));
    } else {
      const draftKey = mod === 'ortho' ? ORTHO_DRAFT_KEY : NEURO_DRAFT_KEY;
      window.localStorage.setItem(userKey(draftKey), JSON.stringify({ assessmentId: pendingPaidId }));
    }
    setPendingPaidId(null);
    navigate(route);
  };

  const openCardio = r => {
    try {
      window.localStorage.setItem(userKey(CARDIO_DRAFT_KEY), JSON.stringify({
        id:               r.id,
        data:             r.data,
        impairmentProfile: r.impairmentProfile || null,
        chatHistory:      r.chatHistory       || [],
      }));
    } catch (e) {
      if (e.name === 'QuotaExceededError') { alert('Storage is full. Please clear old assessments to continue.'); return; }
    }
    navigate('/cardio-form');
  };

  const deleteOrtho = id => {
    const updated = orthoRecords.filter(r => r.id !== id);
    setOrthoRecords(updated);
    window.localStorage.setItem(userKey(SAVED_ORTHO_KEY), JSON.stringify(updated));
    pushAssessments(SAVED_ORTHO_KEY, updated);
  };
  const deleteNeuro = id => {
    const updated = neuroRecords.filter(r => r.id !== id);
    setNeuroRecords(updated);
    window.localStorage.setItem(userKey(SAVED_NEURO_KEY), JSON.stringify(updated));
    pushAssessments(SAVED_NEURO_KEY, updated);
  };
  const deleteCardio = id => {
    const updated = cardioRecords.filter(r => r.id !== id);
    setCardioRecords(updated);
    window.localStorage.setItem(userKey(SAVED_CARDIO_KEY), JSON.stringify(updated));
    pushAssessments(SAVED_CARDIO_KEY, updated);
  };

  const allHistory = [
    ...orthoRecords.map(r => ({ ...r, _module: 'Ortho', _open: openOrtho, _delete: () => deleteOrtho(r.id), _tc: '#0369a1', _tb: '#e0f2fe' })),
    ...neuroRecords.map(r => {
      const pc = NEURO_COLORS[r.pattern] || { color: '#6366f1', bg: '#eef2ff' };
      return { ...r, _module: 'Neuro', _open: openNeuro, _delete: () => deleteNeuro(r.id), _tc: pc.color, _tb: pc.bg };
    }),
    ...cardioRecords.map(r => ({ ...r, _module: 'Cardio', _open: openCardio, _delete: () => deleteCardio(r.id), _tc: '#059669', _tb: '#ecfdf5' })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  const filtered = allHistory
    .filter(r => historyFilter === 'all' || r._module.toLowerCase() === historyFilter)
    .filter(r => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        (r.patientName || '').toLowerCase().includes(q) ||
        (r.diagnosis   || '').toLowerCase().includes(q) ||
        (r.zone        || '').toLowerCase().includes(q) ||
        (r.pattern     || '').toLowerCase().includes(q) ||
        (r._module     || '').toLowerCase().includes(q)
      );
    });

  const total = allHistory.length;

  const FILTER_TABS = [
    { key: 'all',    label: `All (${total})` },
    { key: 'ortho',  label: `Ortho (${orthoRecords.length})` },
    { key: 'neuro',  label: `Neuro (${neuroRecords.length})` },
    { key: 'cardio', label: `Cardio (${cardioRecords.length})` },
  ];

  return (
    <>
    <style>{`
      /* ── Desktop default ── */
      .am-btn-short { display: none; }
      .am-btn-long  { display: inline; }

      /* History list — native CSS Grid, no table */
      .am-history-row { display: grid; grid-template-columns: auto 1fr 130px minmax(0,160px) 84px 100px 96px; align-items: center; }
      .am-history-thead div { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

      /* ════════════════════════════════════════
         TABLET  601px – 1024px
         Release fixed layout, 2-col grid,
         full card content, horizontal scroll table
      ════════════════════════════════════════ */
      @media (max-width: 1024px) {
        .am-root         { height: auto !important; min-height: 100vh !important; overflow-x: hidden !important; overflow-y: auto !important; }
        .am-nav          { padding: 0 16px !important; }
        .am-nav-name     { display: none !important; }
        .am-page-pad     { flex: unset !important; overflow: visible !important; display: block !important; padding: 16px 14px 40px !important; }

        .am-module-grid  { grid-template-columns: repeat(2, 1fr) !important; gap: 12px !important; }
        .am-module-card  { height: auto !important; padding: 18px 16px !important; flex-direction: column !important; gap: 12px !important; }
        .am-module-badge { display: inline-flex !important; }
        .am-module-desc  { display: block !important; font-size: 0.76rem !important; }
        .am-module-pills { display: flex !important; }
        .am-module-card-icon  { width: 40px !important; height: 40px !important; font-size: 18px !important; }
        .am-module-card-title { font-size: 0.92rem !important; }
        .am-module-btn   { width: 100% !important; padding: 11px !important; font-size: 0.82rem !important; min-height: 44px !important; }

        .am-history-card    { flex: unset !important; height: auto !important; overflow: hidden !important; margin-bottom: 20px !important; }
        .am-history-wrap    { overflow-y: visible !important; flex: unset !important; }
        .am-history-header  { padding: 12px 14px 10px !important; }
        .am-history-subtext { display: none !important; }
        .am-filter-btn      { font-size: 0.7rem !important; padding: 5px 11px !important; min-height: 30px !important; }
        .am-td-zone         { display: none !important; }
        .am-history-row     { grid-template-columns: auto 1fr minmax(0,140px) 84px 100px 96px !important; }
      }

    `}</style>
    <div className="am-root" style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#f6f8fb', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", color: '#0f172a' }}>

      {/* ══ NAV ══════════════════════════════════════════════ */}
      <nav className="am-nav" style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #eaecf0',
        padding: '0 24px',
        height: 56,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxSizing: 'border-box',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/logo192.png" alt="AssessMonk" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain' }} />
          <span style={{ fontWeight: 800, fontSize: '0.95rem', letterSpacing: '-0.01em' }}>AssessMonk</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {getAuthName() && (
            <span className="am-nav-name" style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>
              {getAuthName()}
            </span>
          )}
          {installPrompt && (
            <button
              type="button"
              onClick={handleInstall}
              style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4f46e5', background: 'transparent', border: '1.5px solid #c7d2fe', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, minHeight: 44 }}
            >
              ⬇ Install
            </button>
          )}
          <button
            type="button"
            onClick={() => authLogout(navigate)}
            style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', transition: 'all 0.15s', minHeight: 44 }}
            onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fca5a5'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
          >
            Sign out
          </button>
        </div>
      </nav>

      <div className="am-page-pad" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxWidth: 1080, width: '100%', margin: '0 auto', padding: '24px 24px 0', boxSizing: 'border-box' }}>

      

        {/* ══ USAGE FETCH FAILED ══════════════════════════════ */}
        {usageFailed && !ADMIN_EMAILS.includes(getAuthEmail()) && (
          <div style={{ marginBottom: 14, padding: '9px 16px', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: '#fffbeb', border: '1px solid #fde68a' }}>
            <span style={{ fontSize: '0.76rem', fontWeight: 600, color: '#92400e' }}>⚠ Could not load plan info</span>
            <button
              onClick={() => {
                setUsageFailed(false);
                setUsageLoaded(false);
                fetch(`${API_BASE}/api/user/usage`, { headers: getAuthHeaders() })
                  .then(r => r.ok ? r.json() : Promise.reject())
                  .then(d => { setUsage(d); setUsageFailed(false); })
                  .catch(() => setUsageFailed(true))
                  .finally(() => setUsageLoaded(true));
              }}
              style={{ fontSize: '0.72rem', fontWeight: 700, padding: '4px 12px', borderRadius: 8, border: '1px solid #fcd34d', background: '#fef3c7', color: '#92400e', cursor: 'pointer' }}
            >Retry</button>
          </div>
        )}

        {/* ══ USAGE BAR (free tier only) ══════════════════════ */}
        {usage && !usage.isAdmin && usage.tier === 'free' && (
          <div style={{ marginBottom: 14, padding: '10px 16px', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', background: usage.used >= usage.limit ? '#fef2f2' : '#f8fafc', border: `1px solid ${usage.used >= usage.limit ? '#fca5a5' : '#e2e8f0'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 90, height: 5, borderRadius: 99, background: '#e2e8f0', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(100, (usage.used / usage.limit) * 100)}%`, borderRadius: 99, background: usage.used >= usage.limit ? '#ef4444' : '#4f46e5', transition: 'width 0.4s' }} />
              </div>
              <span style={{ fontSize: '0.76rem', fontWeight: 700, color: usage.used >= usage.limit ? '#dc2626' : '#475569' }}>
                {usage.used >= usage.limit ? 'All forms locked — 3 free assessments used' : `${usage.used} of ${usage.limit} free assessments used`}
              </span>
            </div>
            {usage.used < usage.limit && (
              <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>{usage.limit - usage.used} remaining</span>
            )}
          </div>
        )}

        {/* ══ PENDING PAID SESSION RECOVERY ══════════════════ */}
        {pendingPaidId && (
          <div style={{ marginBottom: 14, padding: '12px 16px', borderRadius: 12, background: '#f0fdf4', border: '1px solid #86efac' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#166534' }}>
                💳 You have 1 paid session (₹19) waiting — pick a module to start
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                {[{ mod: 'ortho', label: 'Ortho' }, { mod: 'neuro', label: 'Neuro' }, { mod: 'cardio', label: 'Cardio' }].map(({ mod, label }) => (
                  <button key={mod} onClick={() => resumePaidSession(mod)} style={{ fontSize: '0.72rem', fontWeight: 700, padding: '5px 12px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer' }}>{label}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ══ MODULE CARDS ════════════════════════════════════ */}
        <div className="am-module-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
          <ModuleCard
            icon="🦴" title="Orthopaedic"
            desc="Musculoskeletal evaluation with ROM, MMT, and special tests."
            accentColor="#2563eb" accentBg="#eff6ff"
            features={['ROM', 'MMT', 'Special Tests', 'AI Diagnosis']}
            count={orthoRecords.length} onNew={newOrtho}
          />
          <ModuleCard
            icon="🧠" title="Neurology"
            desc="UMN/LMN patterns, cranial nerves, sensory and motor systems."
            accentColor="#7c3aed" accentBg="#f5f3ff"
            features={['UMN', 'LMN', 'Cerebellar', 'Sensory']}
            count={neuroRecords.length} onNew={newNeuro}
          />
          <ModuleCard
            icon="🫀" title="Cardio Pulmonary"
            desc="Vital signs, respiratory assessment and cardiac screening."
            accentColor="#059669" accentBg="#ecfdf5"
            features={['Vitals', 'Respiratory', 'Cardiac', 'SpO₂']}
            count={cardioRecords.length} onNew={newCardio}
          />
        </div>

        {/* ══ REFER & EARN BANNER ══════════════════════════════ */}
        <div
          onClick={() => navigate('/refer')}
          style={{ marginBottom: 16, padding: '13px 18px', borderRadius: 14, background: 'linear-gradient(135deg,#eef2ff,#f5f3ff)', border: '1.5px solid #c7d2fe', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, transition: 'box-shadow 0.15s, transform 0.15s', boxShadow: '0 1px 4px rgba(79,70,229,0.08)' }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(79,70,229,0.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(79,70,229,0.08)'; e.currentTarget.style.transform = 'none'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 11, background: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>🎁</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: '0.88rem', color: '#0f172a' }}>Refer &amp; Earn</div>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 1 }}>Invite colleagues and earn ₹100 for every paid referral</div>
            </div>
          </div>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#4f46e5', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            View Dashboard <span style={{ fontSize: '1rem' }}>→</span>
          </div>
        </div>

        {/* ══ ASSESSMENT HISTORY ══════════════════════════════ */}
        <div className="am-history-card" style={{ flex: 1, minHeight: 0, background: '#fff', borderRadius: 18, border: '1px solid #eaecf0', boxShadow: '0 1px 4px rgba(15,23,42,0.05)', display: 'flex', flexDirection: 'column', marginBottom: 24 }}>

          {/* header */}
          <div className="am-history-header" style={{ padding: '14px 20px 10px', borderBottom: '1px solid #f1f5f9' }}>
            {/* row 1: title + search */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ flex: '0 0 auto' }}>
                <h2 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 800, color: '#0f172a' }}>Assessment History</h2>
                <p className="am-history-subtext" style={{ margin: 0, fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>Click any row to reopen</p>
              </div>
              <div className="am-search-box" style={{ position: 'relative', flex: '1 1 0', minWidth: 0 }}>
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search patient, diagnosis..."
                  style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 99, border: '1px solid #e2e8f0', fontSize: '0.78rem', outline: 'none', background: '#f8fafc', color: '#0f172a', fontFamily: 'inherit' }}
                  onFocus={e => { e.currentTarget.style.borderColor = '#4f46e5'; e.currentTarget.style.background = '#fff'; }}
                  onBlur={e  => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = '#f8fafc'; }}
                />
                {search && (
                  <button onClick={() => setSearch('')} aria-label="Clear search" style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1rem', lineHeight: 1, padding: 0, minWidth: '32px', minHeight: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                )}
              </div>
            </div>
            {/* row 2: filter tabs */}
            <div className="am-filter-row" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {FILTER_TABS.map(t => (
                <button key={t.key} onClick={() => setHistoryFilter(t.key)}
                  className="am-filter-btn"
                  style={{
                    padding: '6px 14px', minHeight: '32px', borderRadius: 99, border: 'none',
                    fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', transition: 'all 0.15s',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: historyFilter === t.key ? '#0f172a' : '#f1f5f9',
                    color: historyFilter === t.key ? '#fff' : '#64748b',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* list */}
          {filtered.length > 0 ? (
            <div className="am-history-wrap" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <div className="am-history-thead am-history-row" style={{ background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                {[['Module', '11px 12px 11px 20px'], ['Patient', '10px 12px'], ['Zone / Pattern', '10px 12px'], ['Diagnosis', '10px 12px'], ['Date', '10px 12px'], ['Status', '10px 12px'], ['', '10px 20px 10px 12px']].map(([h, pad]) => (
                  <div key={h} style={{ padding: pad, textAlign: h === '' ? 'right' : 'left', fontSize: '0.67rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                    {h}
                  </div>
                ))}
              </div>
              <div>
                {filtered.map(r => (
                  <HistoryRow key={r.id} record={r} module={r._module} tagColor={r._tc} tagBg={r._tb} onOpen={() => r._open(r)} onDelete={r._delete} onPrint={() => handlePrint(r)} />
                ))}
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, textAlign: 'center', padding: '52px 20px' }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, margin: '0 auto 12px' }}>📋</div>
              <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: '0.9rem', color: '#475569' }}>No assessments yet</p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>
                Start an assessment using the cards above and save it to see history here.
              </p>
            </div>
          )}
        </div>

      </div>

      {/* ══ PRINT + RESPONSIVE CSS ══════════════════════════ */}
      <style>{`
        @media print {
          body > *:not(#home-print-portal) { display: none !important; }
          #home-print-portal { display: block !important; padding: 24px; box-sizing: border-box; font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; font-size: 11px; color: #0f172a; background: #fff; }
          #home-print-portal > * { page-break-inside: avoid; }
        }
        @media screen {
          #home-print-portal { display: none; }
        }

        /* ════════════════════════════════════════
           PHONE  ≤ 600px
           Compact 3-col grid, card-style history rows
        ════════════════════════════════════════ */
        @media (max-width: 600px) {
          .am-nav      { padding: 0 12px !important; }
          .am-page-pad { padding: 12px 10px 40px !important; }

          .am-module-grid       { grid-template-columns: repeat(3, 1fr) !important; gap: 8px !important; margin-bottom: 10px !important; }
          .am-module-card       { padding: 10px 8px !important; gap: 6px !important; border-radius: 14px !important; }
          .am-module-card-icon  { width: 32px !important; height: 32px !important; font-size: 16px !important; border-radius: 9px !important; }
          .am-module-card-title { font-size: 0.72rem !important; margin: 0 !important; overflow-wrap: break-word !important; }
          .am-module-badge      { font-size: 0.56rem !important; padding: 1px 5px !important; }
          .am-module-desc       { display: none !important; }
          .am-module-pills      { display: none !important; }
          .am-module-btn        { padding: 6px 4px !important; font-size: 0.67rem !important; min-height: 34px !important; border-radius: 8px !important; }
          .am-btn-long          { display: none !important; }
          .am-btn-short         { display: inline !important; }

          .am-history-header    { padding: 10px 12px 8px !important; }
          .am-history-subtext   { display: none !important; }
          .am-filter-btn        { padding: 5px 10px !important; font-size: 0.7rem !important; min-height: 30px !important; }

          /* Fix card collapsing — override inline minHeight: 0 */
          .am-history-card            { min-height: unset !important; overflow: visible !important; }
          .am-history-wrap            { overflow: visible !important; }

          /* History rows → 4-row cards */
          .am-history-thead           { display: none !important; }
          .am-history-row             { grid-template-columns: auto 1fr auto !important; grid-template-rows: auto auto auto auto !important; padding: 13px 16px !important; gap: 3px 8px !important; align-items: start !important; }
          .am-td-module               { grid-column: 1; grid-row: 1; padding: 0 !important; padding-top: 2px !important; }
          .am-td-patient              { grid-column: 2; grid-row: 1; padding: 0 !important; }
          .am-td-patient span         { font-size: 0.88rem !important; font-weight: 700 !important; color: #1e293b !important; }
          .am-td-status               { grid-column: 3; grid-row: 1; padding: 0 !important; }
          .am-td-zone                 { display: block !important; grid-column: 1 / 3; grid-row: 2; padding: 0 !important; }
          .am-td-zone span            { font-size: 0.73rem !important; color: #6366f1 !important; font-weight: 600 !important; white-space: nowrap !important; overflow: hidden !important; text-overflow: ellipsis !important; }
          .am-td-date                 { grid-column: 3; grid-row: 2; padding: 0 !important; text-align: right !important; }
          .am-td-date span            { font-size: 0.68rem !important; color: #94a3b8 !important; white-space: nowrap !important; }
          .am-td-diag                 { grid-column: 1 / -1; grid-row: 3; padding: 0 !important; }
          .am-td-diag span            { font-size: 0.75rem !important; font-weight: 500 !important; color: #334155 !important; }
          .am-td-actions              { grid-column: 1 / -1; grid-row: 4; padding: 0 !important; justify-content: flex-start !important; margin-top: 4px !important; }
          .am-td-actions > div        { gap: 6px !important; }
          .am-td-actions button       { width: auto !important; height: 30px !important; padding: 0 12px !important; font-size: 0.68rem !important; font-weight: 600 !important; background: #f1f5f9 !important; border: 1px solid #e2e8f0 !important; color: #475569 !important; border-radius: 7px !important; display: inline-flex !important; align-items: center !important; gap: 3px !important; }
          .am-td-actions button span  { color: #10b981 !important; font-size: 0.78rem !important; }
        }

        @media (min-width: 601px) { .am-btn-short { display: none; } }

        /* ── Very small phones ≤ 360px ── */
        @media (max-width: 360px) {
          .am-module-grid { gap: 6px !important; }
          .am-module-card { padding: 8px 6px !important; }
          .am-filter-btn  { padding: 4px 7px !important; font-size: 0.65rem !important; }
        }
      `}</style>

    </div>

    {showUpgrade && (
      <div
        onClick={() => { setShowUpgrade(false); setSelectedPlan(null); setUpgradeModule(null); setPayError(null); }}
        style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{ background: '#fff', borderRadius: '20px', padding: '28px 28px 24px', maxWidth: '440px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', animation: 'fadeIn 0.2s ease-out' }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{ width: 40, height: 40, borderRadius: '12px', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', flexShrink: 0 }}>🔒</div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800', color: '#0f172a' }}>Free Limit Reached</h3>
              <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#64748b' }}>You've used all {usage?.limit || 3} lifetime free assessments</p>
            </div>
          </div>

          {/* What you get on upgrade — side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>

            {/* Student */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '13px 12px' }}>
              <p style={{ margin: '0 0 10px', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#4f46e5' }}>Student</p>
              {['Unlimited assessments', 'PDF & history sync', 'Full AI output'].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '7px', alignItems: 'flex-start', marginBottom: '7px' }}>
                  <span style={{ color: '#4f46e5', fontSize: '0.72rem', marginTop: '2px', flexShrink: 0 }}>✓</span>
                  <span style={{ fontSize: '0.78rem', color: '#334155', lineHeight: 1.4 }}>{item}</span>
                </div>
              ))}
              <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>₹1,299</span>
                <span style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 500 }}>/year</span>
              </div>
            </div>

            {/* Clinician */}
            <div style={{ background: '#faf5ff', border: '1.5px solid #c4b5fd', borderRadius: '12px', padding: '13px 12px' }}>
              <p style={{ margin: '0 0 10px', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#7c3aed' }}>Clinician</p>
              {['Unlimited assessments', 'PDF & history sync'].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '7px', alignItems: 'flex-start', marginBottom: '7px' }}>
                  <span style={{ color: '#7c3aed', fontSize: '0.72rem', marginTop: '2px', flexShrink: 0 }}>✓</span>
                  <span style={{ fontSize: '0.78rem', color: '#334155', lineHeight: 1.4 }}>{item}</span>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '7px', alignItems: 'flex-start', marginBottom: '7px' }}>
                <span style={{ color: '#7c3aed', fontSize: '0.72rem', marginTop: '2px', flexShrink: 0 }}>✓</span>
                <span style={{ fontSize: '0.78rem', color: '#1e293b', lineHeight: 1.4, fontWeight: 800 }}>Higher AI output and clinical reasoning</span>
              </div>
              <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #ddd6fe' }}>
                <span style={{ fontSize: '0.92rem', fontWeight: 800, color: '#0f172a' }}>₹1,999</span>
                <span style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 500 }}>/year</span>
              </div>
            </div>

          </div>

          {/* ₹19 quick-pay option */}
          <button
            type="button"
            onClick={() => setSelectedPlan('diagnosis_single')}
            style={{ width: '100%', padding: '12px 14px', borderRadius: '11px', border: selectedPlan === 'diagnosis_single' ? '2px solid #059669' : '1.5px solid #d1fae5', background: selectedPlan === 'diagnosis_single' ? '#ecfdf5' : '#f0fdf4', cursor: 'pointer', textAlign: 'left', marginBottom: '10px', transition: 'all 0.15s', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <div>
              <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#065f46' }}>Pay ₹19 — one assessment now</div>
              <div style={{ fontSize: '0.7rem', color: '#059669', marginTop: 2 }}>Form access + AI + refine + chat + PDF for this session</div>
            </div>
            <span style={{ fontSize: '1.1rem', fontWeight: 900, color: '#059669' }}>₹19</span>
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '10px' }}>
            <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
            <span style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 600 }}>or yearly plan</span>
            <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
          </div>

          {/* Yearly plan picker */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
            {[
              { id: 'student_yearly',   label: 'Student',   price: '₹1,299', period: 'per year' },
              { id: 'clinician_yearly', label: 'Clinician', price: '₹1,999', period: 'per year' },
            ].map(plan => (
              <button
                key={plan.id}
                type="button"
                onClick={() => setSelectedPlan(plan.id)}
                style={{ padding: '10px 8px', borderRadius: '10px', border: selectedPlan === plan.id ? '2px solid #4f46e5' : '1.5px solid #e2e8f0', background: selectedPlan === plan.id ? '#eef2ff' : '#fafafa', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}
              >
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{plan.label}</div>
                <div style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', margin: '4px 0 1px' }}>{plan.price}</div>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{plan.period}</div>
              </button>
            ))}
          </div>

          {/* Pay button */}
          <button
            type="button"
            onClick={() => {
              if (!selectedPlan || paymentLoading) return;
              if (selectedPlan === 'diagnosis_single') {
                const newId = crypto.randomUUID();
                setPaymentLoading(true);
                payForDiagnosis(newId, {
                  onSuccess: () => { setPaymentLoading(false); navigateToFormAfterPay(newId); },
                  onCancel:  () => setPaymentLoading(false),
                  onError:   (msg) => { setPaymentLoading(false); setPayError(msg || 'Payment failed. Please try again.'); },
                });
              } else {
                handlePayment(selectedPlan);
              }
            }}
            disabled={!selectedPlan || paymentLoading}
            style={{ width: '100%', padding: '12px', background: !selectedPlan || paymentLoading ? '#c7d2fe' : 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '0.9rem', cursor: !selectedPlan || paymentLoading ? 'not-allowed' : 'pointer', marginBottom: '10px', boxShadow: !selectedPlan || paymentLoading ? 'none' : '0 10px 25px -5px rgba(79,70,229,0.4)', transition: 'all 0.15s' }}
          >
            {paymentLoading ? 'Processing...' : selectedPlan ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                Pay with <img src="/razorpay-logo.png" alt="Razorpay" style={{ height: 16, objectFit: 'contain', verticalAlign: 'middle', filter: 'brightness(0) invert(1)' }} />
              </span>
            ) : 'Select a plan above'}
          </button>

          {/* Razorpay official merchant badge */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
            <a href="https://razorpay.com/" target="_blank" rel="noreferrer">
              <img referrerPolicy="origin" src="https://badges.razorpay.com/badge-dark.png" style={{ height: 45, width: 113 }} alt="Razorpay | Payment Gateway | Neobank" />
            </a>
          </div>

          {payError && (
            <div style={{ marginBottom: 10, padding: '10px 14px', borderRadius: 9, background: '#fef2f2', border: '1px solid #fca5a5', fontSize: '0.78rem', color: '#991b1b', fontWeight: 600 }}>
              ⚠ {payError}
            </div>
          )}
          <button
            type="button"
            onClick={() => { setShowUpgrade(false); setSelectedPlan(null); setUpgradeModule(null); setPayError(null); }}
            style={{ width: '100%', padding: '11px', background: 'transparent', color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: '10px', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer' }}
          >
            Maybe later
          </button>
        </div>
      </div>
    )}

    {printRecord && ReactDOM.createPortal(
      <div id="home-print-portal">
        {printRecord._module === 'Ortho' && <OrthoPrintSummary data={printRecord.data || {}} diagnosisData={printRecord.diagnosisData} zone={printRecord.zone} isSubmitted={printRecord.isSubmitted} />}
        {printRecord._module === 'Neuro' && <NeuroPrintSummary data={printRecord.data || {}} diagnosisData={printRecord.diagnosisData} zone={printRecord.pattern} isSubmitted={printRecord.isSubmitted} />}
        {printRecord._module === 'Cardio' && <CardioPrintSummary data={printRecord.data || {}} impairmentProfile={printRecord.impairmentProfile} />}
      </div>,
      document.body
    )}
    </>
  );
};

export default HomePage;
